import React, { useState, useEffect } from 'react';
import { Landmark, UserRound, ScanFace, LogOut, CloudOff, Search, BellRing, Send } from 'lucide-react';
import { toast } from 'sonner';
import { fmtMoney, TxRow, DayGroups } from './PocketHome';
import { bioSupported, bioEnabled, enableBio, disableBio } from '../../lib/pocketBio';
import { pushSupported, getPushSubscription, enablePush, disablePush } from '../../lib/pocketPush';
import { financeApi } from '../../lib/api';

export const PocketHistory = ({ recent }) => {
  const [q, setQ] = useState('');
  const norm = (s) => (s || '').toString().toLowerCase().replace(/[إأآ]/g, 'ا');
  const nq = norm(q.trim());
  const filtered = !nq ? recent : recent.filter((t) => {
    const hay = norm(`${t.category} ${t.description} ${t.person} ${t.payee}`);
    if (hay.includes(nq)) return true;
    // amount search: "3700" matches 3700 or 3700.5 (original or USD)
    const digits = nq.replace(/[^0-9.]/g, '');
    if (!digits) return false;
    return String(t.amount ?? '').includes(digits) || String(Math.round(t.amount_usd ?? 0)).includes(digits);
  });
  return (
    <div className="pk-view" data-testid="pocket-history-view">
      <div className="pk-topbar">
        <div>
          <div className="pk-greet">Timeline</div>
          <div className="pk-uname">سجل الحركات</div>
        </div>
      </div>
      <div style={{ padding: '0 20px 10px', position: 'relative' }}>
        <Search size={15} style={{ position: 'absolute', top: '50%', right: 34, transform: 'translateY(-70%)', color: 'var(--pk-mut)' }} />
        <input
          type="text"
          className="pk-input"
          style={{ paddingRight: 40 }}
          placeholder="ابحث بالاسم أو المبلغ أو الوصف…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid="pocket-history-search"
        />
      </div>
      {filtered.length === 0
        ? <div className="pk-empty">{nq ? `لا نتائج لـ «${q}»` : 'لا يوجد معاملات بعد'}</div>
        : <DayGroups items={filtered}
            metaFor={(t) => `${new Date(t.created_at || t.date).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })} · ${t.type === 'income' ? 'دخل' : t.wallet === 'reserve' ? 'Reserve' : 'Personal'}`} />}
    </div>
  );
};

const WalletCard = ({ mag, icon: Icon, tag, title, data }) => (
  <div className={`pk-card ${mag ? 'mag' : 'gold'}`} style={{ padding: '17px 18px 19px', marginBottom: 12 }}>
    <div className="pk-card-orb" />
    <div className="pk-scan" />
    <div className="pk-card-tag"><Icon size={12} /> {tag}</div>
    <div className="pk-balval" style={{ fontSize: 32 }}><span dir="ltr">{fmtMoney(data?.balance)}</span></div>
    <div className="pk-balsub">{title}</div>
    <div style={{ display: 'flex', gap: 16, marginTop: 12, fontSize: 11.5, color: 'var(--pk-mut)' }}>
      <span>دخل: <b className="pk-num" style={{ color: 'var(--pk-text)' }} dir="ltr">{fmtMoney(data?.credited)}</b></span>
      <span>صرف: <b className="pk-num" style={{ color: 'var(--pk-text)' }} dir="ltr">{fmtMoney(data?.spent)}</b></span>
    </div>
  </div>
);

export const PocketWallet = ({ wallets, user, queueCount, onSyncNow, onLogout }) => {
  const [bioOn, setBioOn] = useState(bioEnabled());
  const [pushOn, setPushOn] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const pct = Math.round((wallets?.reserve_percentage || 0.2) * 100);

  useEffect(() => { getPushSubscription().then((s) => setPushOn(!!s)); }, []);

  const togglePush = async () => {
    if (pushBusy) return;
    setPushBusy(true);
    try {
      if (pushOn) {
        await disablePush();
        setPushOn(false);
        toast('تم إيقاف إشعارات الملخص الأسبوعي');
      } else {
        await enablePush();
        setPushOn(true);
        toast.success('تم التفعيل ✓ — رح يوصلك ملخص مصاريفك كل يوم جمعة حتى لو التطبيق مسكّر');
      }
    } catch (e) {
      if (e?.message === 'permission-denied') toast.error('رفضت إذن الإشعارات — فعّلها من إعدادات الجهاز');
      else toast.error('ما قدرت أفعّل الإشعارات على هذا الجهاز');
    } finally { setPushBusy(false); }
  };

  const testPush = async () => {
    try {
      const r = await financeApi.pushTest();
      toast.success(`انبعت إشعار تجريبي لـ ${r.data.sent} جهاز ✓ — سكّر التطبيق وشوفه`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'فشل إرسال الإشعار التجريبي');
    }
  };

  const toggleBio = async () => {
    if (bioOn) {
      disableBio(); setBioOn(false);
      toast('تم إيقاف قفل البصمة');
      return;
    }
    try {
      await enableBio(user);
      setBioOn(true);
      sessionStorage.setItem('pocket_unlocked', '1');
      toast.success('تم تفعيل Face ID / البصمة ✓ — التطبيق رح ينقفل تلقائيًا عند كل فتح');
    } catch {
      toast.error('ما قدرت أفعّل البصمة — جهازك لازم يدعم Face ID / Touch ID');
    }
  };

  return (
    <div className="pk-view" data-testid="pocket-wallet-view">
      <div className="pk-topbar">
        <div>
          <div className="pk-greet">Wallets · {pct}/{100 - pct}</div>
          <div className="pk-uname">المحافظ والإعدادات</div>
        </div>
      </div>
      <div style={{ padding: '0 20px' }}>
        <WalletCard icon={UserRound} tag="Personal" title="محفظتي الشخصية" data={wallets?.personal} />
        <WalletCard mag icon={Landmark} tag="Reserve" title="احتياطي الشغل" data={wallets?.reserve} />
      </div>

      <div className="pk-sect">الأمان</div>
      <div style={{ padding: '0 20px' }}>
        {bioSupported() ? (
          <button className="pk-card" onClick={toggleBio} data-testid="pocket-bio-toggle"
            style={{ width: '100%', padding: '15px 16px', display: 'flex', alignItems: 'center', gap: 13, cursor: 'pointer', fontFamily: 'inherit', color: 'inherit', textAlign: 'inherit' }}>
            <ScanFace size={22} style={{ color: bioOn ? 'var(--pk-green)' : 'var(--pk-mut)', filter: 'none' }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14.5, fontWeight: 800 }}>قفل Face ID / البصمة</div>
              <div style={{ fontSize: 11.5, color: 'var(--pk-mut)', marginTop: 2 }}>{bioOn ? 'مفعّل — التطبيق ينفتح بالبصمة فقط' : 'افتح Pocket Up بوجهك أو بصمتك بدل الباسورد'}</div>
            </div>
            <span className="pk-pill" style={{ color: bioOn ? 'var(--pk-green)' : 'var(--pk-mut)', borderColor: bioOn ? 'rgba(16,185,129,0.4)' : 'var(--pk-line)' }}>
              {bioOn ? 'ON' : 'OFF'}
            </span>
          </button>
        ) : (
          <div style={{ fontSize: 12, color: 'var(--pk-mut)' }}>جهازك الحالي لا يدعم القفل البيومتري</div>
        )}

        {pushSupported() ? (
          <>
            <button className="pk-card" onClick={togglePush} data-testid="pocket-push-toggle"
              style={{ width: '100%', padding: '15px 16px', display: 'flex', alignItems: 'center', gap: 13, cursor: 'pointer', fontFamily: 'inherit', color: 'inherit', textAlign: 'inherit', marginTop: 12 }}>
              <BellRing size={22} style={{ color: pushOn ? 'var(--pk-green)' : 'var(--pk-mut)', filter: 'none' }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14.5, fontWeight: 800 }}>ملخص أسبوعي على جهازك</div>
                <div style={{ fontSize: 11.5, color: 'var(--pk-mut)', marginTop: 2 }}>
                  {pushOn ? 'مفعّل — كل يوم جمعة يوصلك إشعار بمصاريف الأسبوع حتى لو التطبيق مسكّر' : 'إشعار Push بملخص مصاريف الأسبوع حتى والتطبيق مسكّر'}
                </div>
              </div>
              <span className="pk-pill" style={{ color: pushOn ? 'var(--pk-green)' : 'var(--pk-mut)', borderColor: pushOn ? 'rgba(16,185,129,0.4)' : 'var(--pk-line)' }}>
                {pushBusy ? '…' : pushOn ? 'ON' : 'OFF'}
              </span>
            </button>
            {pushOn && (
              <button className="pk-btn ghost" onClick={testPush} data-testid="pocket-push-test"
                style={{ marginTop: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                <Send size={14} /> جرب — ابعت لي الملخص الآن
              </button>
            )}
          </>
        ) : (
          <div style={{ fontSize: 12, color: 'var(--pk-mut)', marginTop: 12 }} data-testid="pocket-push-unsupported">
            إشعارات Push: على iPhone لازم تثبّت التطبيق على الشاشة الرئيسية أولًا (iOS 16.4+) وتفتحه من الأيقونة
          </div>
        )}

        {queueCount > 0 && (
          <button className="pk-card" onClick={onSyncNow} data-testid="pocket-sync-now"
            style={{ width: '100%', padding: '15px 16px', display: 'flex', alignItems: 'center', gap: 13, cursor: 'pointer', fontFamily: 'inherit', color: 'inherit', textAlign: 'inherit', marginTop: 12 }}>
            <CloudOff size={22} style={{ color: 'var(--pk-amber)' }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14.5, fontWeight: 800 }}>معاملات بانتظار الرفع</div>
              <div style={{ fontSize: 11.5, color: 'var(--pk-mut)', marginTop: 2 }}>{queueCount} حركة محفوظة محليًا — اضغط للمزامنة الآن</div>
            </div>
          </button>
        )}

        <button className="pk-btn danger" onClick={onLogout} data-testid="pocket-logout" style={{ marginTop: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
          <LogOut size={16} /> تسجيل الخروج
        </button>
      </div>
    </div>
  );
};
